/** Subscribed discovery-pack SKU — auto-adds electric whisk and shaker bottle, then applies SIPCLUB. */
const DISCOVERY_PACK_SKU = 'discovery-pack';
const DRINK_MIXER_SKU = 'drink-mixer';
const SHAKER_HANDLE = 'matcha-shaker-bottle';
const DISCOVERY_GIFT_CODE = 'SIPCLUB';

/** @type {boolean} */
let discoveryWhiskAddInFlight = false;

/** Stops re-applying SIPCLUB on every cart update when the code stays inapplicable. */
let discoveryGiftCodeSettled = false;

/**
 * @returns {Promise<{ items?: Array<{ sku?: string, quantity?: number, product_handle?: string }>, item_count?: number } | null>}
 */
async function fetchCartJson() {
  try {
    const url = new URL('/cart.js', window.location.origin);
    url.searchParams.set('_', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

/**
 * @param {{ sku?: string, product_handle?: string } | null | undefined} line
 * @returns {string}
 */
function lineSkuNorm(line) {
  return line?.sku ? String(line.sku).toLowerCase().trim() : '';
}

/**
 * @param {{ selling_plan_allocation?: { selling_plan?: { id?: number | string } } | null, selling_plan?: number | string, selling_plan_id?: number | string } | null | undefined} line
 * @returns {boolean}
 */
function lineIsSubscription(line) {
  const allocation = line?.selling_plan_allocation;
  if (allocation && allocation.selling_plan && allocation.selling_plan.id) {
    return true;
  }

  const planId = line?.selling_plan_id ?? line?.selling_plan;
  if (planId == null || planId === '' || planId === 0 || planId === '0') {
    return false;
  }

  return true;
}

/**
 * @param {{ items?: Array<{ selling_plan_allocation?: unknown }> } | null | undefined} cart
 * @returns {boolean}
 */
function cartReportsSellingPlans(cart) {
  const items = cart?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return true;
  }

  return items.some((line) => line != null && Object.prototype.hasOwnProperty.call(line, 'selling_plan_allocation'));
}

/**
 * @param {{ items?: Array<{ sku?: string, quantity?: number, product_handle?: string }> } | null | undefined} cart
 * @param {string} skuWant
 * @returns {boolean}
 */
function cartHasSkuWithQty(cart, skuWant) {
  const want = String(skuWant || '')
    .toLowerCase()
    .trim();

  if (!want) {
    return false;
  }

  for (const line of cart?.items || []) {
    const qty = Math.max(0, parseInt(String(line?.quantity ?? ''), 10) || 0);
    if (qty <= 0) {
      continue;
    }

    if (lineSkuNorm(line) === want) {
      return true;
    }
  }

  return false;
}

/**
 * Discovery pack SKU present on a selling plan. One-time lines do not qualify.
 * @param {{ items?: Array<{ sku?: string, quantity?: number, selling_plan_allocation?: { selling_plan?: { id?: number | string } } | null, selling_plan?: number | string, selling_plan_id?: number | string }> } | null | undefined} cart
 * @returns {boolean}
 */
function cartHasSubscribedDiscoveryPack(cart) {
  for (const line of cart?.items || []) {
    const qty = Math.max(0, parseInt(String(line?.quantity ?? ''), 10) || 0);
    if (qty <= 0) {
      continue;
    }

    if (lineSkuNorm(line) !== DISCOVERY_PACK_SKU) {
      continue;
    }

    if (lineIsSubscription(line)) {
      return true;
    }
  }

  return false;
}

/**
 * @returns {HTMLElement | null}
 */
function getCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  return drawer instanceof HTMLElement ? drawer : null;
}

/**
 * @param {string} name
 * @returns {string | null}
 */
function drawerData(name) {
  const raw = (getCartDrawer()?.getAttribute(name) || '').trim();
  return raw || null;
}

/**
 * @returns {string | null}
 */
function getDrinkMixerVariantId() {
  return drawerData('data-shinzo-drink-mixer-variant-id');
}

/**
 * @returns {string | null}
 */
function getShakerVariantId() {
  return drawerData('data-shinzo-shaker-variant-id');
}

/**
 * @returns {string | null}
 */
function getShakerSku() {
  return drawerData('data-shinzo-shaker-sku');
}

/**
 * @param {{ items?: unknown[], item_count?: number } | null} cart
 * @param {Record<string, string> | undefined} sections
 */
function dispatchCartUpdate(cart, sections) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        cart,
        data: {
          sections,
          source: 'shinzo-cart-drawer-discovery-whisk',
          itemCount: cart?.item_count,
          openCartDrawer: false,
        },
      },
    })
  );
}

/**
 * @param {{ sku?: string, url?: string, handle?: string, quantity?: number } | null | undefined} line
 * @returns {boolean}
 */
function lineIsShaker(line) {
  const qty = Math.max(0, parseInt(String(line?.quantity ?? ''), 10) || 0);
  if (qty <= 0) {
    return false;
  }

  const shakerSku = getShakerSku();
  if (shakerSku && lineSkuNorm(line) === shakerSku.toLowerCase()) {
    return true;
  }

  const handle = String(line?.handle || '').toLowerCase();
  const url = String(line?.url || '').toLowerCase();
  return handle === SHAKER_HANDLE || url.includes(`/products/${SHAKER_HANDLE}`);
}

/**
 * @param {{ items?: Array<{ sku?: string, url?: string, handle?: string, quantity?: number }> } | null | undefined} cart
 * @returns {boolean}
 */
function cartHasShaker(cart) {
  for (const line of cart?.items || []) {
    if (lineIsShaker(line)) {
      return true;
    }
  }

  return false;
}

/**
 * @param {{ items?: Array<{ sku?: string, url?: string, handle?: string, quantity?: number }> } | null | undefined} cart
 * @returns {Array<{ id: number | string, quantity: number }>}
 */
function missingGiftItems(cart) {
  /** @type {Array<{ id: number | string, quantity: number }>} */
  const items = [];
  const whiskId = getDrinkMixerVariantId();
  if (whiskId && !cartHasSkuWithQty(cart, DRINK_MIXER_SKU)) {
    items.push({ id: Number(whiskId) || whiskId, quantity: 1 });
  }

  const shakerId = getShakerVariantId();
  if (shakerId && !cartHasShaker(cart)) {
    items.push({ id: Number(shakerId) || shakerId, quantity: 1 });
  }

  return items;
}

/**
 * @param {{ discount_codes?: Array<{ code?: string, applicable?: boolean }> } | null | undefined} cart
 * @returns {{ code?: string, applicable?: boolean } | null}
 */
function sipclubEntry(cart) {
  const codes = cart?.discount_codes;
  if (!Array.isArray(codes)) {
    return null;
  }

  return (
    codes.find((entry) => String(entry?.code || '').toLowerCase() === DISCOVERY_GIFT_CODE.toLowerCase()) || null
  );
}

/**
 * Keeps codes already on the cart and adds SIPCLUB. cart/update replaces the whole list.
 * @param {{ discount_codes?: Array<{ code?: string }> } | null | undefined} cart
 * @returns {string}
 */
function discountCodeList(cart) {
  /** @type {string[]} */
  const codes = [];
  const seen = new Set();

  for (const entry of cart?.discount_codes || []) {
    const code = String(entry?.code || '').trim();
    if (!code) {
      continue;
    }

    const key = code.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    codes.push(code);
  }

  if (!seen.has(DISCOVERY_GIFT_CODE.toLowerCase())) {
    codes.push(DISCOVERY_GIFT_CODE);
  }

  return codes.join(',');
}

/**
 * @returns {string[]}
 */
function cartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  }

  return sectionIds;
}

/**
 * @param {string} configuredUrl
 * @param {string} fallback
 * @returns {string}
 */
function ajaxCartUrl(configuredUrl, fallback) {
  const base = configuredUrl || fallback;
  return base.endsWith('.js') ? base : `${base}.js`;
}

/**
 * @param {{ items?: Array<{ sku?: string, quantity?: number, product_handle?: string }> } | null | undefined} [cart]
 */
async function maybeAutoAddDrinkMixer(cart) {
  if (discoveryWhiskAddInFlight) {
    return;
  }

  if (!getDrinkMixerVariantId() && !getShakerVariantId()) {
    return;
  }

  let resolvedCart = cart?.items ? cart : await fetchCartJson();
  if (resolvedCart && (!cartReportsSellingPlans(resolvedCart) || !Array.isArray(resolvedCart.discount_codes))) {
    resolvedCart = await fetchCartJson();
  }

  if (!resolvedCart || !cartHasSubscribedDiscoveryPack(resolvedCart)) {
    return;
  }

  const gifts = missingGiftItems(resolvedCart);
  const code = sipclubEntry(resolvedCart);
  const giftsInCart = cartHasSkuWithQty(resolvedCart, DRINK_MIXER_SKU) || cartHasShaker(resolvedCart);
  const needsCode = !code || code.applicable === false;
  if (gifts.length === 0 && (!needsCode || discoveryGiftCodeSettled || !giftsInCart)) {
    return;
  }

  const sectionIds = cartSectionIds();
  const themeRoutes = typeof window.Theme !== 'undefined' ? window.Theme?.routes : undefined;
  const addUrl = ajaxCartUrl(themeRoutes?.cart_add_url ? String(themeRoutes.cart_add_url) : '', '/cart/add.js');
  const updateUrl = ajaxCartUrl(
    themeRoutes?.cart_update_url ? String(themeRoutes.cart_update_url) : '',
    '/cart/update.js'
  );

  discoveryWhiskAddInFlight = true;

  try {
    /** @type {Record<string, string> | undefined} */
    let sections;
    let addedGifts = false;

    if (gifts.length) {
      /** @type {{ items: Array<{ id: number | string, quantity: number }>, sections?: string }} */
      const payload = { items: gifts };
      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
      }

      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return;
      }

      /** @type {{ sections?: Record<string, string>, status?: number, message?: string } | null} */
      let result = null;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        result = await response.json();
      }

      if (result?.status && result?.message) {
        return;
      }

      sections = result?.sections;
      addedGifts = true;
      resolvedCart = (await fetchCartJson()) || resolvedCart;
    }

    const latestCode = sipclubEntry(resolvedCart);
    if (addedGifts || !latestCode || latestCode.applicable === false) {
      /** @type {{ discount: string, sections?: string }} */
      const discountPayload = { discount: discountCodeList(resolvedCart) };
      if (sectionIds.length) {
        discountPayload.sections = sectionIds.join(',');
      }

      const discountResponse = await fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(discountPayload),
      });

      if (discountResponse.ok) {
        discoveryGiftCodeSettled = true;
        const discountType = discountResponse.headers.get('content-type') || '';
        if (discountType.includes('application/json')) {
          const discountResult = await discountResponse.json();
          if (discountResult?.sections) {
            sections = discountResult.sections;
          }
          if (discountResult?.items) {
            resolvedCart = discountResult;
          }
        }
      }
    }

    const updatedCart = resolvedCart?.items ? resolvedCart : await fetchCartJson();
    dispatchCartUpdate(updatedCart, sections);
  } catch {
    // Drawer will reconcile on next open or cart refresh
  } finally {
    discoveryWhiskAddInFlight = false;
  }
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  if (!(event instanceof CustomEvent) || !event.detail) {
    return;
  }

  if (event.detail?.data?.source === 'shinzo-cart-drawer-discovery-whisk') {
    return;
  }

  const cart =
    event.detail.resource?.items
      ? event.detail.resource
      : event.detail.cart?.items
        ? event.detail.cart
        : null;

  if (cart) {
    void maybeAutoAddDrinkMixer(cart);
    return;
  }

  void maybeAutoAddDrinkMixer();
}

function bindCartDrawerOpenHandler() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement) || drawer.dataset.shinzoDiscoveryWhiskOpenBound === 'true') {
    return;
  }

  drawer.dataset.shinzoDiscoveryWhiskOpenBound = 'true';
  drawer.addEventListener('dialog:open', () => {
    void maybeAutoAddDrinkMixer();
  });
}

document.addEventListener('cart:update', handleCartUpdate);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindCartDrawerOpenHandler);
} else {
  bindCartDrawerOpenHandler();
}
